// Marketing → Inbox: replies people left on our Threads posts, each with a
// suggested answer. Approve sends the reply on the next inbox run; Skip
// leaves the person unanswered. Sensitive ones are washed amber.
import { useEffect, useState } from 'react';
import { ErrorNote, Field, Note } from '../../components/ui';
import { errorText } from '../../lib/backbarApi';
import { chars, hstDateTime, mk, THREADS_MAX, type InboxItem } from '../../lib/marketing';
import { MkPage } from './shared';

const VIEWS = [
  { value: 'pending', label: 'Waiting' },
  { value: 'approved', label: 'Approved' },
  { value: 'posted', label: 'Sent' },
  { value: 'skipped', label: 'Skipped' },
];

export function MarketingInbox() {
  const [view, setView] = useState('pending');
  const [items, setItems] = useState<InboxItem[] | null>(null);
  const [pending, setPending] = useState<number | undefined>();
  const [err, setErr] = useState<unknown>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setItems(null);
    mk.inbox(view)
      .then((i) => { if (!alive) return; setItems(i); setErr(null); if (view === 'pending') setPending(i.length); })
      .catch((e) => alive && setErr(e));
    if (view !== 'pending') mk.inbox('pending').then((i) => alive && setPending(i.length)).catch(() => undefined);
    return () => { alive = false; };
  }, [view, tick]);

  return (
    <MkPage title="Inbox" blurb="Replies to our Threads posts, with a suggested answer for each. Nothing is sent until you approve it." pendingReplies={pending}>
      <div className="lp-tabs" role="tablist" aria-label="Which replies" style={{ marginBottom: 14 }}>
        {VIEWS.map((v) => (
          <button key={v.value} type="button" role="tab" aria-selected={view === v.value} className={view === v.value ? 'on' : ''} onClick={() => setView(v.value)}>
            {v.label}{v.value === 'pending' && pending ? ` (${pending})` : ''}
          </button>
        ))}
      </div>
      <ErrorNote error={err} notConfigured="Marketing is not set up on the server yet." />
      <section className="bk-card">
        <div className="bk-card-head"><h2 className="bk-card-title">{VIEWS.find((v) => v.value === view)?.label} replies</h2>{items && <span className="bk-card-sub">{items.length}</span>}</div>
        {!items && !err && <div className="bk-card-body"><p className="lp-empty">Loading…</p></div>}
        {items && items.length === 0 && (
          <div className="bk-card-body"><p className="lp-empty">{view === 'pending' ? 'No replies are waiting. New ones are pulled in every 4 hours once Threads is connected.' : 'Nothing here.'}</p></div>
        )}
        {items?.map((it) => <ReplyRow key={it.id} item={it} onChanged={() => setTick((t) => t + 1)} />)}
      </section>
    </MkPage>
  );
}

function ReplyRow({ item, onChanged }: { item: InboxItem; onChanged: () => void }) {
  const [draft, setDraft] = useState(item.draft_reply ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sensitive = item.classification === 'sensitive';
  const count = chars(draft);
  const over = count > THREADS_MAX;
  const editable = item.reply_state === 'pending';

  async function set(reply_state: 'approved' | 'skipped' | 'pending') {
    setBusy(true); setErr(null);
    try {
      await mk.inboxSet(item.id, { reply_state, ...(editable && draft !== (item.draft_reply ?? '') ? { draft_reply: draft } : {}) });
      onChanged();
    } catch (e) { setErr(errorText(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className={`mk-reply${sensitive ? ' mk-reply--sensitive' : ''}`}>
      <div className="mk-reply-head">
        <span className="mk-reply-author">@{item.author_handle ?? 'unknown'}</span>
        {item.classification && <span className={`mk-chip mk-chip--${item.classification}`}>{item.classification === 'benign' ? 'Friendly' : item.classification === 'sensitive' ? 'Sensitive' : 'Spam'}</span>}
        <span>{hstDateTime(item.received_at)}</span>
      </div>
      <p className="mk-reply-text">{item.text}</p>
      {item.parent && <p className="mk-reply-parent">On our post: “{item.parent.body.length > 140 ? `${item.parent.body.slice(0, 140)}…` : item.parent.body}”</p>}
      {sensitive && editable && <Note tone="warn">This one needs care. Read the suggested answer closely, or skip it.</Note>}
      {editable ? (
        <>
          <Field label={sensitive ? 'Suggested response' : 'Draft reply'}>
            <textarea className="lp-ta" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write a reply" />
          </Field>
          <div className={`mk-count${over ? ' mk-count--over' : ''}`}>{count} / {THREADS_MAX}</div>
          <div className="lp-row" style={{ marginTop: 6 }}>
            <button type="button" className="lp-btn lp-btn--primary" disabled={busy || over || !draft.trim()} onClick={() => void set('approved')}>Approve reply</button>
            <button type="button" className="lp-btn" disabled={busy} onClick={() => void set('skipped')}>Skip</button>
          </div>
        </>
      ) : (
        <>
          {item.draft_reply && <p className="mk-reply-parent">Our reply: “{item.draft_reply}”</p>}
          {(item.reply_state === 'approved' || item.reply_state === 'skipped') && (
            <button type="button" className="lp-btn lp-btn--small" disabled={busy} onClick={() => void set('pending')}>Move back to waiting</button>
          )}
        </>
      )}
      {err && <Note tone="warn">{err}</Note>}
    </div>
  );
}
